import React, { useState } from "react";
import {
    Box,
    Container,
    Typography,
    TextField,
    Button,
    Card,
    CardContent,
    Grid,
    Tabs,
    Tab,
    Alert,
    InputAdornment,
    IconButton,
} from "@mui/material";
import { motion } from "framer-motion";
import { Visibility, VisibilityOff, Lock, Person, Email, Business, Phone } from "@mui/icons-material";

const LoginPage = () => {
    const [activeTab, setActiveTab] = useState(0);
    const [showPassword, setShowPassword] = useState(false);
    const [loginForm, setLoginForm] = useState({ username: "", password: "" });
    const [requestForm, setRequestForm] = useState({
        companyName: "",
        contactName: "",
        email: "",
        phone: "",
        contractNumber: "",
        message: "",
    });
    const [loginError, setLoginError] = useState("");
    const [requestSuccess, setRequestSuccess] = useState(false);

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
        setLoginError("");
        setRequestSuccess(false);
    };

    const handleLoginSubmit = (e) => {
        e.preventDefault();
        // TODO: Implement actual authentication logic
        if (!loginForm.username || !loginForm.password) {
            setLoginError("Please enter both username and password");
            return;
        }
        // Placeholder for authentication
        setLoginError("Authentication not yet implemented. Please contact support.");
    };

    const handleRequestSubmit = (e) => {
        e.preventDefault();
        // TODO: Implement actual form submission logic
        if (!requestForm.companyName || !requestForm.contactName || !requestForm.email) {
            setLoginError("Please fill in all required fields");
            return;
        }
        // Placeholder for form submission
        setRequestSuccess(true);
        setRequestForm({
            companyName: "",
            contactName: "",
            email: "",
            phone: "",
            contractNumber: "",
            message: "",
        });
    };

    return (
        <>
            {/* Hero Section */}
            <Box
                sx={{
                    position: "relative",
                    height: { xs: "250px", sm: "300px", md: "350px" },
                    background: "linear-gradient(135deg, #000000 0%, #1a1a1a 50%, #facd00 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                    textAlign: "center",
                    padding: { xs: 2, sm: 5 },
                }}
            >
                <motion.div
                    initial={{ opacity: 0, y: -50 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1 }}
                >
                    <Typography
                        variant="h1"
                        fontWeight="bold"
                        sx={{
                            fontSize: { xs: "2rem", sm: "3rem", md: "4rem" },
                            textShadow: "2px 2px 4px rgba(0, 0, 0, 0.5)",
                        }}
                    >
                        Customer Portal
                    </Typography>
                    <Typography
                        variant="h5"
                        mt={2}
                        sx={{
                            color: "#facd00",
                            fontSize: { xs: "1rem", sm: "1.5rem", md: "1.8rem" },
                        }}
                    >
                        Access Your Ticketing System & Knowledge Base
                    </Typography>
                </motion.div>
            </Box>

            {/* Main Content */}
            <Container sx={{ py: 8 }}>
                <Grid container justifyContent="center">
                    <Grid item xs={12} md={8} lg={6}>
                        <motion.div
                            initial={{ opacity: 0, y: 50 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8 }}
                        >
                            <Card sx={{ boxShadow: 6, borderRadius: 3 }}>
                                <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                                    <Tabs
                                        value={activeTab}
                                        onChange={handleTabChange}
                                        centered
                                        sx={{
                                            "& .MuiTab-root": {
                                                fontSize: "1.1rem",
                                                fontWeight: "bold",
                                                py: 2,
                                            },
                                            "& .Mui-selected": {
                                                color: "#facd00",
                                            },
                                            "& .MuiTabs-indicator": {
                                                backgroundColor: "#facd00",
                                                height: 3,
                                            },
                                        }}
                                    >
                                        <Tab label="Sign In" />
                                        <Tab label="Request Access" />
                                    </Tabs>
                                </Box>

                                <CardContent sx={{ p: 4 }}>
                                    {/* Sign In Tab */}
                                    {activeTab === 0 && (
                                        <Box>
                                            <Typography variant="h5" fontWeight="bold" gutterBottom>
                                                Contract Customer Sign In
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                                Access your support ticketing system, knowledge base, and contract resources.
                                            </Typography>

                                            {loginError && (
                                                <Alert severity="error" sx={{ mb: 3 }}>
                                                    {loginError}
                                                </Alert>
                                            )}

                                            <form onSubmit={handleLoginSubmit}>
                                                <TextField
                                                    fullWidth
                                                    label="Username"
                                                    variant="outlined"
                                                    margin="normal"
                                                    value={loginForm.username}
                                                    onChange={(e) =>
                                                        setLoginForm({ ...loginForm, username: e.target.value })
                                                    }
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <Person />
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                    required
                                                />
                                                <TextField
                                                    fullWidth
                                                    label="Password"
                                                    type={showPassword ? "text" : "password"}
                                                    variant="outlined"
                                                    margin="normal"
                                                    value={loginForm.password}
                                                    onChange={(e) =>
                                                        setLoginForm({ ...loginForm, password: e.target.value })
                                                    }
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <Lock />
                                                            </InputAdornment>
                                                        ),
                                                        endAdornment: (
                                                            <InputAdornment position="end">
                                                                <IconButton
                                                                    onClick={() => setShowPassword(!showPassword)}
                                                                    edge="end"
                                                                >
                                                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                                                </IconButton>
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                    required
                                                />

                                                <Button
                                                    type="submit"
                                                    fullWidth
                                                    variant="contained"
                                                    size="large"
                                                    sx={{
                                                        mt: 3,
                                                        mb: 2,
                                                        py: 1.5,
                                                        backgroundColor: "#facd00",
                                                        color: "#000",
                                                        fontWeight: "bold",
                                                        fontSize: "1.1rem",
                                                        "&:hover": {
                                                            backgroundColor: "#e6b800",
                                                        },
                                                    }}
                                                >
                                                    Sign In
                                                </Button>

                                                <Box sx={{ textAlign: "center", mt: 2 }}>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Forgot your password?{" "}
                                                        <a href="/contact/support" style={{ color: "#facd00", textDecoration: "none" }}>
                                                            Contact Support
                                                        </a>
                                                    </Typography>
                                                </Box>
                                            </form>
                                        </Box>
                                    )}

                                    {/* Request Access Tab */}
                                    {activeTab === 1 && (
                                        <Box>
                                            <Typography variant="h5" fontWeight="bold" gutterBottom>
                                                Request Portal Access
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                                Have a Data Speedway service contract? Request access to our customer portal
                                                for ticketing, knowledge base, and support resources.
                                            </Typography>

                                            {requestSuccess && (
                                                <Alert severity="success" sx={{ mb: 3 }}>
                                                    Your access request has been submitted! Our team will review and contact you
                                                    within 24 hours.
                                                </Alert>
                                            )}

                                            {loginError && (
                                                <Alert severity="error" sx={{ mb: 3 }}>
                                                    {loginError}
                                                </Alert>
                                            )}

                                            <form onSubmit={handleRequestSubmit}>
                                                <TextField
                                                    fullWidth
                                                    label="Company Name *"
                                                    variant="outlined"
                                                    margin="normal"
                                                    value={requestForm.companyName}
                                                    onChange={(e) =>
                                                        setRequestForm({ ...requestForm, companyName: e.target.value })
                                                    }
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <Business />
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                    required
                                                />
                                                <TextField
                                                    fullWidth
                                                    label="Contact Name *"
                                                    variant="outlined"
                                                    margin="normal"
                                                    value={requestForm.contactName}
                                                    onChange={(e) =>
                                                        setRequestForm({ ...requestForm, contactName: e.target.value })
                                                    }
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <Person />
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                    required
                                                />
                                                <TextField
                                                    fullWidth
                                                    label="Email Address *"
                                                    type="email"
                                                    variant="outlined"
                                                    margin="normal"
                                                    value={requestForm.email}
                                                    onChange={(e) =>
                                                        setRequestForm({ ...requestForm, email: e.target.value })
                                                    }
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <Email />
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                    required
                                                />
                                                <TextField
                                                    fullWidth
                                                    label="Phone Number"
                                                    variant="outlined"
                                                    margin="normal"
                                                    value={requestForm.phone}
                                                    onChange={(e) =>
                                                        setRequestForm({ ...requestForm, phone: e.target.value })
                                                    }
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <Phone />
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                />
                                                <TextField
                                                    fullWidth
                                                    label="Contract Number (if available)"
                                                    variant="outlined"
                                                    margin="normal"
                                                    value={requestForm.contractNumber}
                                                    onChange={(e) =>
                                                        setRequestForm({ ...requestForm, contractNumber: e.target.value })
                                                    }
                                                />
                                                <TextField
                                                    fullWidth
                                                    label="Additional Information"
                                                    variant="outlined"
                                                    margin="normal"
                                                    multiline
                                                    rows={4}
                                                    value={requestForm.message}
                                                    onChange={(e) =>
                                                        setRequestForm({ ...requestForm, message: e.target.value })
                                                    }
                                                    placeholder="Tell us about your service contract and what access you need..."
                                                />

                                                <Button
                                                    type="submit"
                                                    fullWidth
                                                    variant="contained"
                                                    size="large"
                                                    sx={{
                                                        mt: 3,
                                                        mb: 2,
                                                        py: 1.5,
                                                        backgroundColor: "#facd00",
                                                        color: "#000",
                                                        fontWeight: "bold",
                                                        fontSize: "1.1rem",
                                                        "&:hover": {
                                                            backgroundColor: "#e6b800",
                                                        },
                                                    }}
                                                >
                                                    Submit Access Request
                                                </Button>

                                                <Box sx={{ textAlign: "center", mt: 2 }}>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Questions?{" "}
                                                        <a href="/contact/support" style={{ color: "#facd00", textDecoration: "none" }}>
                                                            Contact our support team
                                                        </a>
                                                    </Typography>
                                                </Box>
                                            </form>
                                        </Box>
                                    )}
                                </CardContent>
                            </Card>

                            {/* Info Section */}
                            <Box sx={{ mt: 4, textAlign: "center" }}>
                                <Typography variant="h6" fontWeight="bold" gutterBottom>
                                    What's Included in the Customer Portal?
                                </Typography>
                                <Grid container spacing={3} sx={{ mt: 2 }}>
                                    <Grid item xs={12} sm={4}>
                                        <Box>
                                            <Typography variant="subtitle1" fontWeight="bold" color="primary">
                                                🎫 Ticketing System
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Submit and track support requests
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Box>
                                            <Typography variant="subtitle1" fontWeight="bold" color="primary">
                                                📚 Knowledge Base
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Access guides and documentation
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Box>
                                            <Typography variant="subtitle1" fontWeight="bold" color="primary">
                                                📊 Contract Resources
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                View service agreements and SLAs
                                            </Typography>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Box>
                        </motion.div>
                    </Grid>
                </Grid>
            </Container>
        </>
    );
};

export default LoginPage;
